import { queryOptions } from "@tanstack/react-query";
import { fetchPageData } from "./pageDataClient";
import { pageDataKeys } from "./pageDataKeys";
import { fetchLocalSlots } from "./localSlotsQuery";
import type { PageInitialDataResult } from "./pageInitialDataTypes";
import type { DefaultPageInitialData } from "./defaultPageInitialData";

/**
 * Query options for the generic catch-all CMS content page.
 * Keyed on the full lookupPath (the URL remainder after the React basepath).
 */
export function catchAllPageQueryOptions(
  lookupPath: string,
  location: { pathname: string; searchStr: string } = {
    pathname: lookupPath.startsWith("/") ? lookupPath : `/${lookupPath}`,
    searchStr: "",
  },
) {
  return queryOptions({
    queryKey: pageDataKeys.catchAll(lookupPath, location.pathname, location.searchStr),
    queryFn: async () => {
      // The cms-structure label is the lookup path with a leading slash; the language prefix is
      // not part of it (the BFF redirect handling normalizes it).
      const label = lookupPath.startsWith("/") ? lookupPath : `/${lookupPath}`;
      const [envelope, localSlots] = await Promise.all([
        fetchPageData<PageInitialDataResult<DefaultPageInitialData>>(
          `/api/page-data/${lookupPath}${location.searchStr}`,
        ),
        fetchLocalSlots(label),
      ]);
      // Any local-structure failure leaves the envelope's slots untouched (this covers the curated
      // notFound page, which carries its own slots). On success the page-scoped slots come from the
      // local structure and the envelope only keeps its globals as the shell's no-cache fallback.
      if (localSlots && envelope.status === "OK") {
        envelope.metadata.slots = [
          ...localSlots.slots,
          ...envelope.metadata.slots.filter((slot) => slot.scope === "global"),
        ];
      }
      return envelope;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}
